/**
 * Catálogo de PRECIOS (importes) — fuente única de verdad en el código.
 *
 * Antes cada importe vivía duplicado y hardcodeado en varios sitios (los packs en
 * `MINUTE_PACKAGES`, el trial en `payment-service`, la etiqueta del trial en
 * `package-selector`, el JSON-LD en `structured-data`). Aquí queda uno solo, con
 * override por variable de entorno para poder cambiar un precio sin desplegar
 * código nuevo.
 *
 * ⚠️ INVARIANTE OPERATIVA: estos importes NO cobran NI se registran. El cargo
 * real lo aplica DodoPayments según el precio configurado en cada producto de su
 * dashboard (y RevenueCat según el producto de la store), y el `amount` de la
 * Transaction es el que cobró el proveedor, no el de aquí (ADR-021). Esto es
 * ESCAPARATE: pinta el precio en la UI y el structured data. Si cambias un precio
 * de verdad, cámbialo en el producto del proveedor; esta env var sólo alinea lo
 * que se anuncia. Ver `docs/DODO_SETUP.md`.
 *
 * DÓNDE SE PINTA ESTO, desde la enmienda a la regla 5 del ADR-021 (2026-08-07):
 *
 *  - **Landings, JSON-LD y Términos**: aquí manda este catálogo. Son páginas
 *    estáticas de SEO y ADR-020/021 prohíben pedirle precios al proveedor en su
 *    render.
 *  - **Paywall web** (`package-selector`): manda lo que va a cobrar Dodo
 *    (`/api/payments/prices`), y este catálogo es el **fallback** — lo que se
 *    pinta cuando el proveedor no contesta, tarda o no se conoce el país. Con
 *    precio por país en Dodo, lo de aquí ya no coincide con el cargo fuera de la
 *    zona euro.
 *  - **Paywall nativo**: ni una cosa ni otra; es la UI de RevenueCat y sus
 *    precios salen de la store.
 *
 * Prefijo `NEXT_PUBLIC_` porque los precios se pintan en componentes cliente
 * (`package-selector`, `landing-pricing`, `checkout-modal`) y Next solo inyecta
 * en el bundle de cliente las env vars con ese prefijo. No son secretos: el
 * comprador los ve en el checkout.
 *
 * Lectura LAZY (dentro de la función, nunca como const de módulo) por el mismo
 * motivo que `lib/pricing.ts`: que Next inline correctamente en build y que los
 * tests puedan mutar `process.env` por test sin `vi.resetModules()`.
 */

export type PriceCurrency = 'EUR' | 'USD'

/**
 * Normaliza a `PriceCurrency` o devuelve `null`. Vive junto al tipo, como
 * `parseCountryCode` en `country.ts` y `parseCurrencyCode` en `money.ts`: las
 * fuentes son heterogéneas (query params, storage escrito a mano) y el parseo
 * pertenece al borde del dominio, no a un hook de React.
 */
export function parsePriceCurrency(value: unknown): PriceCurrency | null {
  if (typeof value !== 'string') return null

  const normalized = value.trim().toUpperCase()

  return normalized === 'EUR' || normalized === 'USD' ? normalized : null
}

/** Paquete de "1 minuto de prueba": no está en `MINUTE_PACKAGES` (no es catálogo). */
export const TRIAL_PACKAGE_ID = 'trial_1min'

export type PricedPackageId =
  | typeof TRIAL_PACKAGE_ID
  | 'package_3min'
  | 'package_6min'

/** Precios vigentes cuando no hay override por env var. */
const DEFAULT_PRICES: Record<PricedPackageId, Record<PriceCurrency, number>> = {
  [TRIAL_PACKAGE_ID]: { EUR: 1.49, USD: 1.49 },
  package_3min: { EUR: 4.99, USD: 5.99 },
  package_6min: { EUR: 8.99, USD: 9.99 },
}

/**
 * Overrides por env var. Cada `process.env.NEXT_PUBLIC_*` se escribe LITERAL a
 * propósito: Next hace sustitución textual en build, así que un acceso dinámico
 * (`process.env[nombre]`) no se inyectaría nunca en el bundle de cliente.
 */
function priceOverrides(): Record<PricedPackageId, Record<PriceCurrency, string | undefined>> {
  return {
    [TRIAL_PACKAGE_ID]: {
      EUR: process.env.NEXT_PUBLIC_PRICE_TRIAL_EUR,
      USD: process.env.NEXT_PUBLIC_PRICE_TRIAL_USD,
    },
    package_3min: {
      EUR: process.env.NEXT_PUBLIC_PRICE_3MIN_EUR,
      USD: process.env.NEXT_PUBLIC_PRICE_3MIN_USD,
    },
    package_6min: {
      EUR: process.env.NEXT_PUBLIC_PRICE_6MIN_EUR,
      USD: process.env.NEXT_PUBLIC_PRICE_6MIN_USD,
    },
  }
}

/**
 * Importe de un paquete en una moneda. Un override vacío, no numérico o <= 0 se
 * ignora y se usa el precio por defecto: una env var mal escrita degrada al
 * catálogo conocido en vez de anunciar 0 € o `NaN` (mismo criterio que
 * `getWebDiscountPercent`).
 */
export function getPrice(packageId: PricedPackageId, currency: PriceCurrency): number {
  return parsePrice(priceOverrides()[packageId][currency]) ?? DEFAULT_PRICES[packageId][currency]
}

function parsePrice(raw: string | undefined): number | null {
  if (!raw) return null

  const parsed = Number.parseFloat(raw.trim())
  if (!Number.isFinite(parsed) || parsed <= 0) return null

  return parsed
}

/**
 * El pack de minutos más barato en una moneda: el «desde» que anuncia una
 * landing. El trial queda fuera: no es catálogo (no se vende a cualquiera).
 */
export function cheapestPackPrice(currency: PriceCurrency): number {
  const packs = (Object.keys(DEFAULT_PRICES) as PricedPackageId[]).filter((id) => id !== TRIAL_PACKAGE_ID)
  return Math.min(...packs.map((id) => getPrice(id, currency)))
}
